<template>
  <div class="relative">
    <button
      type="button"
      class="w-full mt-2 pl-4 pr-10 py-3 rounded-2xl border border-gray-200 bg-white shadow-sm text-left focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
      @click="open = true"
    >
      <span
        v-if="selectedLabel"
        class="text-gray-900"
      >{{ selectedLabel }}</span>
      <span
        v-else
        class="text-gray-400"
      >{{ placeholder }}</span>
      <i class="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
    </button>

    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="open"
          class="fixed inset-0 z-[100]"
        >
          <div
            class="absolute inset-0 bg-black/40"
            @click="close"
          />
          <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="translate-y-full"
            enter-to-class="translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="translate-y-0"
            leave-to-class="translate-y-full"
          >
            <div
              v-if="open"
              class="absolute inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl overflow-hidden"
            >
              <div class="px-5 pt-4 pb-3 border-b">
                <div class="mx-auto h-1 w-10 rounded-full bg-gray-300 mb-3" />
                <div class="flex items-center gap-3">
                  <h3 class="text-base font-semibold text-gray-900 flex-1">
                    {{ title }}
                  </h3>
                  <button
                    class="text-sm text-gray-500 px-2 py-1"
                    @click="close"
                  >
                    Close
                  </button>
                </div>
                <div
                  v-if="search"
                  class="mt-3"
                >
                  <div class="relative">
                    <input
                      ref="searchRef"
                      v-model="query"
                      type="text"
                      placeholder="Search…"
                      class="w-full px-4 py-2 pl-10 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
                    >
                    <i class="fas fa-search absolute left-3 top-2.5 text-gray-400" />
                  </div>
                </div>
              </div>

              <ul class="max-h-[60vh] overflow-y-auto py-1">
                <li
                  v-for="opt in filtered"
                  :key="keyOf(opt)"
                >
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-5 py-3 text-left"
                    :class="isSelected(opt) ? 'bg-blue-50' : ''"
                    @click="select(opt)"
                  >
                    <span class="text-gray-900">{{ labelOf(opt) }}</span>
                    <i
                      v-if="isSelected(opt)"
                      class="fas fa-check text-blue-600"
                    />
                  </button>
                </li>
              </ul>

              <div class="p-4 border-t">
                <button
                  v-if="clearable && modelValue !== null && modelValue !== undefined && modelValue !== ''"
                  class="w-full border border-gray-300 text-gray-700 py-2 rounded-full"
                  @click="clear"
                >
                  Clear
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch } from 'vue'

const props = defineProps({
  modelValue: [String, Number, Boolean, Object, null],
  options: { type: Array, required: true },
  title: { type: String, default: 'Select' },
  placeholder: { type: String, default: 'Select…' },
  labelKey: { type: String, default: 'label' },
  valueKey: { type: String, default: 'value' },
  search: { type: Boolean, default: true },
  clearable: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue', 'change', 'open', 'close'])

const open = ref(false)
const query = ref('')
const searchRef = ref(null)

watch(open, async (v) => {
  if (v) {
    emit('open')
    await nextTick()
    searchRef.value?.focus()
  } else {
    emit('close')
    query.value = ''
  }
})

const labelOf = (o) => (props.labelKey in o ? o[props.labelKey] : String(o))
const valueOf = (o) => (props.valueKey in o ? o[props.valueKey] : o)
const keyOf = (o) => `${valueOf(o)}`

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return props.options
  return props.options.filter((o) => labelOf(o).toLowerCase().includes(q))
})

const isSelected = (o) => valueOf(o) === props.modelValue
const selectedLabel = computed(() => {
  const found = props.options.find((o) => valueOf(o) === props.modelValue)
  return found ? labelOf(found) : ''
})

const select = (o) => {
  const val = valueOf(o)
  emit('update:modelValue', val)
  emit('change', val)
  open.value = false
}
const close = () => (open.value = false)
const clear = () => {
  emit('update:modelValue', '')
  emit('change', '')
  open.value = false
}
</script>
